/**
 * Home Hub panels for the front door (handoffs/09 §4): "My work" (the signed-in
 * person's week from Teamwork + HubSpot, read-only) and "Team tools" (the
 * company's tools, alphabetical). KB home-hub.md.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faBriefcase, faToolbox, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { DWA } from '../theme/dwaTokens';
import { Panel } from '../hubs/HubCards';
import { canSeeItem, findItem } from '../hubs/hubs';
import { fetchMyWork, type MyWork } from '../services/homeApi';
import { openLupaWidget } from '../hubs/hubNav';
import { GENERAL_TIPS, tipsForDeal, tipsForTask, type LupaTip } from './lupaTips';
import { formatHours, formatMoney, formatPercent, loadLabel, shortDate, teamTools, weekLabel } from './homeWork';

const muted: React.CSSProperties = { color: DWA.textSecondary, fontSize: '0.85rem', margin: 0, lineHeight: 1.5 };

const subhead: React.CSSProperties = {
  margin: '1rem 0 0.45rem',
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: DWA.textSecondary,
};

const row: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: '0.75rem',
  padding: '0.5rem 0.65rem',
  borderRadius: DWA.radius,
  background: '#f6f6f4',
  fontSize: '0.85rem',
  textDecoration: 'none',
  color: DWA.ink,
};

const TONE_COLOR = { over: DWA.danger, light: DWA.textMuted, ok: DWA.positive } as const;

const Stat: React.FC<{ label: string; value: string; note?: string }> = ({ label, value, note }) => (
  <div style={{ minWidth: 0 }}>
    <div style={{ fontSize: '0.75rem', color: DWA.textSecondary }}>{label}</div>
    <div style={{ fontSize: '1.35rem', fontWeight: 800, color: DWA.ink, lineHeight: 1.2 }}>{value}</div>
    {note && <div style={{ fontSize: '0.75rem', color: DWA.textMuted }}>{note}</div>}
  </div>
);

const TipChips: React.FC<{ tips: LupaTip[] }> = ({ tips }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
    {tips.map((tip) => (
      <button
        key={tip.label}
        type="button"
        onClick={() => openLupaWidget(tip.prompt)}
        title={tip.prompt}
        style={{
          fontSize: '0.75rem',
          fontWeight: 600,
          padding: '0.3rem 0.65rem',
          borderRadius: 999,
          border: `1px solid ${DWA.gold}`,
          background: '#ffffff',
          color: DWA.ink,
          cursor: 'pointer',
          fontFamily: DWA.font,
        }}
      >
        {tip.label}
      </button>
    ))}
  </div>
);

/** A task or deal row with an "Ask Lupa" toggle that shows tips for it. */
const WorkRow: React.FC<{ title: string; tips: LupaTip[]; children: React.ReactNode }> = ({ title, tips, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'stretch', gap: '0.3rem' }}>
        {children}
        <button
          type="button"
          aria-expanded={open}
          aria-label={`Ask Lupa about ${title}`}
          title="Ways Lupa can help"
          onClick={() => setOpen((v) => !v)}
          style={{
            flexShrink: 0,
            border: 'none',
            borderRadius: DWA.radius,
            background: open ? DWA.gold : '#f6f6f4',
            color: open ? DWA.onGold : DWA.goldInk,
            padding: '0 0.6rem',
            cursor: 'pointer',
          }}
        >
          <FontAwesomeIcon icon={faWandMagicSparkles} aria-hidden="true" />
        </button>
      </div>
      {open && (
        <div style={{ padding: '0.45rem 0.2rem 0.2rem' }}>
          <TipChips tips={tips} />
        </div>
      )}
    </div>
  );
};

export const MyWorkPanel: React.FC = () => {
  const { user } = useAuth();
  const [data, setData] = useState<MyWork | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchMyWork()
      .then((d) => !cancelled && setData(d))
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const today = new Date();
  const pipeline = findItem('dwa-growth-deal-pipeline');
  const showPipelineLink = pipeline?.route && canSeeItem(user, pipeline);

  return (
    <Panel icon={faBriefcase} title="My work" action={data?.week_start && <span style={{ fontSize: '0.8rem', color: DWA.textSecondary }}>{weekLabel(data.week_start, data.week_end)}</span>}>
      <div data-testid="home-my-work">
        {!data && !error && <p style={muted}>Loading your week…</p>}
        {error && <p style={muted}>Couldn't load your week right now.</p>}
        {data && (
          <>
            {data.teamwork ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '0.75rem' }}>
                  <Stat
                    label="Planned this week"
                    value={formatHours(data.teamwork.planned_hours)}
                    note={`of ${formatHours(data.teamwork.target_hours)} · ${formatPercent(data.teamwork.planned_utilization)}`}
                  />
                  <Stat label="Logged so far" value={formatHours(data.teamwork.logged_hours)} note={`${formatHours(data.teamwork.billable_hours)} billable`} />
                  <Stat label="Tasks planned" value={String(data.teamwork.planned_tasks)} note="in Teamwork's workload" />
                </div>
                {(() => {
                  const load = loadLabel(data.teamwork.planned_utilization);
                  const pct = Math.min(100, Math.round(data.teamwork.planned_utilization * 100));
                  return (
                    <div style={{ marginTop: '0.75rem' }}>
                      <div
                        role="meter"
                        aria-label="Planned hours against capacity"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={pct}
                        style={{ height: 8, borderRadius: 999, background: '#ececea', overflow: 'hidden' }}
                      >
                        <div style={{ width: `${pct}%`, height: '100%', background: load.tone === 'over' ? DWA.danger : DWA.gold }} />
                      </div>
                      <div style={{ marginTop: '0.3rem', fontSize: '0.75rem', fontWeight: 700, color: TONE_COLOR[load.tone] }}>{load.label}</div>
                    </div>
                  );
                })()}

                <h3 style={subhead}>Tasks due this week</h3>
                {data.tasks?.status === 'unavailable' && <p style={muted}>Couldn't reach Teamwork for your tasks right now.</p>}
                {data.tasks?.status === 'ok' && data.tasks.items.length === 0 && <p style={muted}>Nothing due this week.</p>}
                {data.tasks?.status === 'ok' && data.tasks.items.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                    {data.tasks.items.map((t) => (
                      <WorkRow key={String(t.id)} title={t.name} tips={tipsForTask(t)}>
                        <a href={t.url ?? undefined} target="_blank" rel="noopener noreferrer" style={{ ...row, flex: 1, minWidth: 0 }}>
                          <span style={{ minWidth: 0 }}>
                            <span style={{ fontWeight: 600 }}>{t.name}</span>
                            {t.project && <span style={{ color: DWA.textMuted }}> · {t.project}</span>}
                          </span>
                          <span style={{ whiteSpace: 'nowrap', fontSize: '0.78rem', fontWeight: t.overdue ? 700 : 400, color: t.overdue ? DWA.danger : DWA.textSecondary }}>
                            {t.overdue ? `Overdue · ${shortDate(t.due_date)}` : shortDate(t.due_date)}
                          </span>
                        </a>
                      </WorkRow>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p style={muted}>
                Your sign-in email isn't matched to a Teamwork person, so there's no workload to show. Ask an admin if that's wrong.
              </p>
            )}

            {data.hubspot && (
              <>
                <h3 style={subhead}>Your open deals</h3>
                <p style={{ ...muted, marginBottom: '0.45rem' }}>
                  <strong style={{ color: DWA.ink }}>{data.hubspot.open_count}</strong> open · {formatMoney(data.hubspot.open_amount)}
                  {data.hubspot.past_due_count > 0 && (
                    <span style={{ color: DWA.danger, fontWeight: 700 }}> · {data.hubspot.past_due_count} past their close date</span>
                  )}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  {data.hubspot.items.map((d) => (
                    <WorkRow key={d.id} title={d.name} tips={tipsForDeal(d, today)}>
                      <div style={{ ...row, flex: 1, minWidth: 0 }}>
                        <span style={{ minWidth: 0 }}>
                          <span style={{ fontWeight: 600 }}>{d.name}</span>
                          {d.stage && <span style={{ color: DWA.textMuted }}> · {d.stage}</span>}
                        </span>
                        <span style={{ whiteSpace: 'nowrap', fontSize: '0.78rem', color: DWA.textSecondary }}>
                          {d.amount != null ? `${formatMoney(d.amount)} · ` : ''}
                          {shortDate(d.close_date)}
                        </span>
                      </div>
                    </WorkRow>
                  ))}
                </div>
                {showPipelineLink && (
                  <Link to={pipeline.route!} style={{ display: 'inline-block', marginTop: '0.5rem', fontSize: '0.8rem', fontWeight: 700, color: DWA.goldInk }}>
                    Deal pipeline
                  </Link>
                )}
              </>
            )}

            <div
              data-testid="home-lupa-tips"
              style={{ marginTop: '1rem', padding: '0.75rem 0.85rem', borderRadius: DWA.radius, background: DWA.goldSoft, border: '1px solid rgba(254,195,92,0.45)' }}
            >
              <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', fontWeight: 700, color: DWA.ink }}>
                <FontAwesomeIcon icon={faWandMagicSparkles} aria-hidden="true" style={{ color: DWA.goldInk, marginRight: '0.4rem' }} />
                Lupa can help
              </p>
              <p style={{ ...muted, fontSize: '0.78rem', margin: '0 0 0.55rem' }}>
                Research, draft emails and copy, pull up SOPs, prep for calls, or plan your week. Use Ask Lupa on any task or deal above, or start here:
              </p>
              <TipChips tips={GENERAL_TIPS} />
            </div>
            <p style={{ ...muted, fontSize: '0.72rem', marginTop: '0.6rem', color: DWA.textMuted }}>
              Workload and hours update daily from Teamwork; tasks are live.
            </p>
          </>
        )}
      </div>
    </Panel>
  );
};

export const TeamToolsPanel: React.FC = () => (
  <Panel icon={faToolbox} title="Team tools">
    <div data-testid="home-team-tools" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.5rem' }}>
      {teamTools().map((tool) => (
        <a
          key={tool.name}
          href={tool.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ ...row, flexDirection: 'column', alignItems: 'flex-start', gap: '0.15rem', padding: '0.65rem 0.75rem' }}
        >
          <span style={{ fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
            {tool.name}
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} aria-hidden="true" style={{ fontSize: '0.65rem', color: DWA.textMuted }} />
          </span>
          <span style={{ fontSize: '0.75rem', color: DWA.textSecondary }}>{tool.description}</span>
        </a>
      ))}
    </div>
  </Panel>
);
